<template>
  <div class="wildlife-form-page">
    <h1>Wildlife Report Form</h1>
    <div class="form-actions">
      <button @click="goToOldAdmin" class="back-button">
        <i class="fas fa-table"></i> View Old Admin
      </button>
      <button @click="toggleViewReports" class="view-reports-button">
        {{ showReports ? 'Hide Reports' : 'View Reports' }}
      </button>
    </div>
    
    <div v-if="loading" class="loading-screen">
      <img style="width:20px; height:20px" src="@/assets/hamster.gif" alt="Loading..." />
      <p>Loading, please wait...</p>
    </div>
    
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="fetchData" class="retry-button">Retry</button>
    </div>
    
    <div v-else>
      <div v-if="!selectedTripGroup" class="trip-group-selection">
        <h2>Select a Trip Group</h2>
        <div v-if="trips.length === 0" class="no-trips-message">
          <p>No trips found for today. Please select a different date.</p>
          <div class="date-picker-container">
            <label>Select Date:</label>
            <DatePicker v-model="selectedDate" @change="fetchTrips" />
          </div>
        </div>
        <div v-else>
          <div class="date-picker-container">
            <label>Select Date:</label>
            <DatePicker v-model="selectedDate" @change="fetchTrips" />
          </div>
          
          <div class="trips-container">
            <div v-for="(trip, tripIndex) in trips" :key="trip.tripId || trip.tripid" class="trip-card">
              <div class="trip-header">
                <h3>Heli #{{ tripIndex + 1 }}</h3>
                <div class="trip-details">
                  <div class="helicopter-info">
                    <strong>{{ trip.helicopter?.callsign || 'Unknown' }} - {{ trip.pilot?.lastname || 'No Pilot' }}</strong>
                  </div>
                  <div class="trip-type">
                    {{ trip.triptype || 'No Trip Type' }}
                  </div>
                </div>
              </div>
              <div class="groups-container">
                <div v-for="(group, groupIndex) in trip.groups" :key="group.groupid || group.groupId" class="group-card">
                  <div class="group-info">
                    <div class="group-title">Group #{{ groupIndex + 1 }}</div>
                    <div class="group-details">
                      <div class="guide-info">
                        <strong>Guide:</strong> {{ group.guide?.lastname || 'No Guide' }}
                      </div>
                      <div class="clients-info">
                        <strong>Clients:</strong> {{ formatClientNames(group.clients) }}
                      </div>
                    </div>
                  </div>
                  <button @click="selectTripGroup(group)" class="select-group-btn">
                    Select This Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <wildlife-form
        v-if="selectedTripGroup"
        :zones="zones"
        :runs="runs"
        :tripGroupId="selectedTripGroup.groupid"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
      
      <!-- Wildlife Reports Section -->
      <div v-if="showReports" class="wildlife-reports-section">
        <h2>Wildlife Reports for {{ selectedDate }}</h2>
        <div v-if="loadingReports" class="loading">
          <p>Loading wildlife reports...</p>
        </div>
        <div v-else-if="wildlifeReports.length === 0" class="no-reports-message">
          <p>No wildlife reports found for this date.</p>
        </div>
        <div v-else class="reports-container">
          <table class="wildlife-reports-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Zone</th>
                <th>Species</th>
                <th>Count</th>
                <th>Surveyor</th>
                <th>Elevation</th>
                <th>Comments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="report in wildlifeReports" :key="report.wildlifeid">
                <td>{{ report.groupDisplay || 'N/A' }}</td>
                <td>
                  <div v-if="report.zones && report.zones.length > 0" class="zones-list">
                    <span v-for="(zone, zoneIndex) in report.zones" :key="zoneIndex" class="zone-item">
                      {{ (zone.zone && zone.zone.zonename) || (zone.zone_name && zone.zone_name !== 'Unknown Zone' ? zone.zone_name : null) || 'Unknown Zone' }}
                    </span>
                  </div>
                  <div v-else>{{ report.heliski_zone || 'N/A' }}</div>
                </td>
                <td>{{ report.species }}</td>
                <td>{{ report.animal_count || 'N/A' }}</td>
                <td>{{ report.surveyor_name || 'N/A' }}</td>
                <td>{{ getElevationName(report.elevation) }}</td>
                <td>{{ report.comments || 'N/A' }}</td>
                <td>
                  <button class="delete-button" @click="deleteReport(report.wildlifeid)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WildlifeForm from './WildlifeForm.vue';
import TripDataService from '@/services/TripDataService.js';
import WildlifeDataService from '@/services/WildlifeDataService.js';
import RunsZonesDataService from '@/services/RunsZonesDataService.js';
import { getElevationName } from '@/utils/wildlifeUtils.js';
import { todayLocalDateString } from '@/utils/dateHelpers.js';
import DatePicker from '../Shared/DatePicker.vue';

export default {
  name: 'WildlifeFormPage',
  components: {
    WildlifeForm,
    DatePicker
  },
  data() {
    return {
      loading: true,
      error: null,
      zones: [],
      runs: [],
      trips: [],
      selectedDate: this.getStoredDate() || todayLocalDateString(),
      selectedTripGroup: null,
      showReports: false,
      wildlifeReports: [],
      loadingReports: false
    };
  },
  methods: {
    getElevationName,
    fetchData() {
      this.loading = true;
      this.error = null;
      
      // Fetch zones and runs in parallel
      Promise.all([
        this.fetchZones(),
        this.fetchAllRuns(),
        this.fetchTrips()
      ])
        .then(() => {
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.error = 'Failed to load data: ' + (error.message || 'Unknown error');
          console.error('Error fetching data:', error);
        });
    },
    fetchZones() {
      return RunsZonesDataService.getAllZones()
        .then(response => {
          this.zones = response.data;
        });
    },
    fetchAllRuns() {
      return RunsZonesDataService.getAllRuns()
        .then(response => {
          this.runs = response.data;
        });
    },
    getStoredDate() {
      return sessionStorage.getItem('selectedDate');
    },
    storeDate(date) {
      sessionStorage.setItem('selectedDate', date);
    },
    fetchTrips() {
      this.storeDate(this.selectedDate);
      return TripDataService.fetchTripsByDate(this.selectedDate)
        .then(response => {
          this.trips = response.data;
        });
    },
    selectTripGroup(group) {
      this.selectedTripGroup = group;
    },
    handleSubmit(reportData) {
      WildlifeDataService.create(reportData)
        .then(() => {
          alert('Wildlife report submitted successfully!');
          // Refresh the reports list if they're being shown
          if (this.showReports) {
            this.fetchWildlifeReports();
          }
          // Reset the form by clearing the selected trip group
          this.selectedTripGroup = null;
        })
        .catch(error => {
          alert('Failed to submit wildlife report: ' + (error.response?.data?.message || error.message || 'Unknown error'));
        });
    },
    handleCancel() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.selectedTripGroup = null;
      }
    },
    toggleViewReports() {
      this.showReports = !this.showReports;
      if (this.showReports) {
        this.fetchWildlifeReports();
      }
    },
    fetchWildlifeReports() {
      this.loadingReports = true;
      WildlifeDataService.getByDate(this.selectedDate)
        .then(response => {
          this.wildlifeReports = response.data;
          console.log('Wildlife reports received:', response.data);
          // Log zone data for debugging
          response.data.forEach(report => {
            if (report.zones) {
              console.log(`Report ${report.wildlifeid} zones:`, report.zones);
            }
          });
          this.loadingReports = false;
        })
        .catch(error => {
          console.error('Error fetching wildlife reports:', error);
          this.loadingReports = false;
          alert('Failed to fetch wildlife reports: ' + (error.response?.data?.message || error.message || 'Unknown error'));
        });
    },
    deleteReport(wildlifeId) {
      if (confirm('Are you sure you want to delete this wildlife report?')) {
        WildlifeDataService.delete(wildlifeId)
          .then(() => {
            // Remove the deleted report from the list
            this.wildlifeReports = this.wildlifeReports.filter(report => report.wildlifeid !== wildlifeId);
            alert('Wildlife report deleted successfully!');
          })
          .catch(error => {
            console.error('Error deleting wildlife report:', error);
            alert('Failed to delete wildlife report: ' + (error.response?.data?.message || error.message || 'Unknown error'));
          });
      }
    },
    goToOldAdmin() {
      this.$router.push('/wildlife-admin-old');
    },
    formatClientNames(clients) {
      if (!clients || clients.length === 0) {
        return 'No Clients';
      }

      const clientNames = clients
        .map(client => client.person?.lastname || 'Unknown')
        .join(', ');

      return clientNames;
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.wildlife-form-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 20px;
  color: #333;
}

.form-actions {
  margin-bottom: 20px;
}

.back-button {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.back-button:hover {
  background-color: #5a6268;
}

.loading, .error {
  text-align: center;
  padding: 40px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin: 20px 0;
}

.error {
  color: #721c24;
  background-color: #f8d7da;
}

.retry-button {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.trip-group-selection {
  margin-bottom: 30px;
}

.date-picker-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.trips-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.trip-card {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  background-color: #f9f9f9;
}

.trip-header {
  margin-bottom: 15px;
}

.trip-header h3 {
  margin-top: 0;
  margin-bottom: 8px;
}

.trip-details {
  font-size: 14px;
  color: #666;
}

.helicopter-info {
  color: #2c3e50;
  margin-bottom: 4px;
}

.trip-type {
  color: #7f8c8d;
  font-style: italic;
}

.groups-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.group-card {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 15px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.group-info {
  text-align: left;
}

.group-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #2c3e50;
  font-size: 16px;
}

.group-details {
  font-size: 13px;
  color: #666;
}

.guide-info, .clients-info {
  margin-bottom: 4px;
}

.guide-info strong, .clients-info strong {
  color: #34495e;
}

.select-group-btn {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: bold;
  align-self: flex-start;
  min-width: 140px;
}

.select-group-btn:hover {
  background-color: #45a049;
}

.no-trips-message, .no-reports-message {
  text-align: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin: 20px 0;
  color: #6c757d;
}

.view-reports-button {
  background-color: #17a2b8;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  margin-left: 10px;
}

.view-reports-button:hover {
  background-color: #138496;
}

.wildlife-reports-section {
  margin-top: 30px;
  border-top: 1px solid #ddd;
  padding-top: 20px;
}

.wildlife-reports-section h2 {
  margin-bottom: 20px;
  color: #333;
}

.wildlife-reports-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  font-size: 0.9em;
}

.wildlife-reports-table th,
.wildlife-reports-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.wildlife-reports-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.wildlife-reports-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.wildlife-reports-table tr:hover {
  background-color: #f1f1f1;
}

.delete-button {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8em;
}

.delete-button:hover {
  background-color: #c82333;
}

.zones-list {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.zone-item {
  background-color: #f1f1f1;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}

.loading-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
}

.loading-screen img {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}
</style>